import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '../i18n';
import { logsAPI } from '../api';

export function LogsPage() {
  const { t } = useTranslation();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyErrors, setOnlyErrors] = useState(true); 
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);

  // Загрузка логов
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await logsAPI.getLogs({
        only_errors: onlyErrors,
        unreviewed_only: unreviewedOnly
      });
      setLogs(res.data || []);
    } catch (e) {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { message: t('logs.loadError', 'Помилка завантаження логів'), severity: 'error' }
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [onlyErrors, unreviewedOnly]);

  // Пометить отдельный лог
  const handleToggleReviewed = async (id, currentStatus) => {
    try {
      await logsAPI.markReviewed(id, !currentStatus);
      setLogs(prev => prev.map(l => l.id === id ? { ...l, is_reviewed: !currentStatus } : l));
      window.dispatchEvent(new CustomEvent('logs-updated'));
    } catch (e) {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { message: t('logs.updateError', 'Помилка оновлення статусу'), severity: 'error' }
      }));
    }
  };

  // Пометить ВСЕ как обработанные
  const handleMarkAllReviewed = async () => {
    try {
      await logsAPI.markAllReviewed();
      setLogs(prev => prev.map(l => ({ ...l, is_reviewed: true })));
      window.dispatchEvent(new CustomEvent('logs-updated'));
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { message: t('logs.markAllSuccess', 'Всі помилки позначено як оброблені'), severity: 'success' }
      }));
    } catch (e) {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { message: t('logs.actionError', 'Помилка виконання дії'), severity: 'error' }
      }));
    }
  };

  // Цвета для бейджей действий
  const getActionBadgeClass = (action) => {
    switch (action) {
      case 'scanner_attack':
      case 'login_failed':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'calculator':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'hydration_request':
        return 'bg-cyan-100 text-cyan-700 border-cyan-200';
      case 'plan_generation':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'race_result':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Цвета для статус-кодов
  const getStatusBadgeClass = (status) => {
    if (status >= 500) return 'bg-red-600 text-white';
    if (status >= 400) return 'bg-amber-500 text-white';
    return 'bg-emerald-600 text-white';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">

        {/* Заголовок и фильтры */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              🛡️ {t('logs.title', 'Журнал активності та безпеки')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('logs.subtitle', 'Автоматичний моніторинг дій атлетів, 4xx/5xx помилок та сканерів')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Тумблер: Только ошибки */}
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                checked={onlyErrors}
                onChange={(e) => setOnlyErrors(e.target.checked)}
                className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
              />
              <span>{t('logs.onlyErrors', 'Тільки помилки')}</span>
            </label>

            {/* Тумблер: Непросмотренные */}
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                checked={unreviewedOnly}
                onChange={(e) => setUnreviewedOnly(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span>{t('logs.unreviewedOnly', 'Тільки неперевірені')}</span>
            </label>

            {/* Кнопка: Обработать все */}
            <button
              onClick={handleMarkAllReviewed}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-1.5 rounded-lg transition-colors shadow-sm"
            >
              <span>✓</span> {t('logs.markAll', 'Обробити всі')}
            </button>

            {/* Кнопка: Обновить */}
            <button
              onClick={fetchLogs}
              title={t('logs.refresh', 'Оновити')}
              className="p-2 text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
            >
              🔄
            </button>
          </div>
        </div>

        {/* Таблица логов */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3">{t('logs.thReviewed', 'Оброблено')}</th>
                  <th className="px-4 py-3">{t('logs.thTime', 'Час (Київ)')}</th>
                  <th className="px-4 py-3">{t('logs.thAction', 'Дія / Подія')}</th>
                  <th className="px-4 py-3">{t('logs.thCode', 'Код')}</th>
                  <th className="px-4 py-3">{t('logs.thIpLocation', 'IP / Локація')}</th>
                  <th className="px-4 py-3">{t('logs.thUrl', 'URL Запиту')}</th>
                  <th className="px-4 py-3">{t('logs.thUser', 'Користувач')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-10 text-gray-400">
                      🎉 {t('logs.noRecords', 'Немає записів за вибраними фільтрами')}
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const isAlert = log.status_code >= 400 || log.is_suspicious || log.action === 'login_failed';
                    return (
                      <tr
                        key={log.id}
                        className={`transition-colors hover:bg-gray-50/80 ${
                          !log.is_reviewed && isAlert ? 'bg-red-50/40' : ''
                        }`}
                      >
                        {/* Галочка обработки */}
                        <td className="px-4 py-3">
                          {isAlert ? (
                            <input
                              type="checkbox"
                              checked={Boolean(log.is_reviewed)}
                              onChange={() => handleToggleReviewed(log.id, log.is_reviewed)}
                              className="w-4 h-4 text-emerald-600 rounded cursor-pointer focus:ring-emerald-500"
                              title={log.is_reviewed ? t('logs.reviewedTooltip', 'Оброблено') : t('logs.markReviewedTooltip', 'Позначити як перевірене')}
                            />
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Время */}
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {dayjs(log.created_at).format('DD.MM.YYYY HH:mm:ss')}
                        </td>

                        {/* Действие */}
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getActionBadgeClass(log.action)}`}>
                            {t(`logs.actions.${log.action}`, log.get_action_display || log.action)}
                          </span>
                        </td>

                        {/* Статус-код */}
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${getStatusBadgeClass(log.status_code)}`}>
                            {log.status_code}
                          </span>
                        </td>

                        {/* IP и гео */}
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-gray-800">{log.ip_address}</div>
                          {log.country && (
                            <div className="text-[11px] text-gray-400 mt-0.5">
                              📍 {log.country}{log.city ? `, ${log.city}` : ''}
                            </div>
                          )}
                        </td>

                        {/* URL */}
                        <td className="px-4 py-3 font-mono text-xs max-w-xs truncate" title={log.url}>
                          <span className="font-bold text-gray-700">{log.method}</span> {log.url}
                        </td>

                        {/* Пользователь */}
                        <td className="px-4 py-3 text-xs">
                          <div className="font-semibold text-gray-800">
                            {log.username || t('logs.guest', 'Гість / Бот')}
                          </div>
                          {log.athlete_name && (
                            <div className="text-indigo-600 text-[11px]">🏃 {log.athlete_name}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}